import { Alert, Box, Button, Card, CircularProgress, Grid, List, Modal, Snackbar, Typography, useMediaQuery } from '@mui/material';
import SearchInput from '../../../shared/SearchInput';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPropertiesWithoutPagination, getResetPmPropertiesState, selectPropertiesState } from '../../../properties/sagas/pmPropertiesSlice';
import { PropertySearchDTO, PropertySearchField, PropertyV2Dto, UpdateApplicationPropertyDto } from '@inosio/payments-sdk';
import CancelIcon from '../../../../icons/close.svg';
import BackIcon from '../../../../icons/back1.svg';
import { LoadingButton } from '@mui/lab';
import { resetUpdateApplicationPropertyStates, selectApplicationsState, updateApplicationProperty } from '../../../../resui/applications/sagas/pm/applicationsSlice';

export interface UpdateApplicationPropertyModalProps {
  open: boolean;
  onClose: () => void;
  oldPropertyName: string;
  appUuid: string;
}

const UpdateApplicationPropertyModal = (props: UpdateApplicationPropertyModalProps) => {
  //local variables
  const matchesMobile = useMediaQuery('(max-width:600px)');
  const dispatch = useDispatch();
  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    padding: '16px',
    minHeight: matchesMobile ? '100%' : '523px',
    pointerEvents: 'auto',
    width: '98%',
    maxWidth: '472px',
    borderRadius: matchesMobile ? '0px' : '24px',
  };

  //local states
  const [activeStep, setActiveStep] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [isSearchValueEmpty, setIsSearchValueEmpty] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<PropertyV2Dto | null>(null);
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  //selectors
  const { isFetchingProperties, pmPropertiesWithoutPagination } = useSelector(selectPropertiesState);
  const { isUpdatingAppProperty, isUpdatingAppPropertySuccess, isUpdatingAppPropertyFailed } = useSelector(selectApplicationsState);

  //useEffects
  useEffect(() => {
    if (searchValue.length === 0) {
      setIsSearchValueEmpty(true);
    } else {
      setIsSearchValueEmpty(false);
    }
  }, [searchValue]);

  useEffect(() => {
    if (isUpdatingAppPropertySuccess) {
      setErrorMessage(null);
      setSnackBarOpen(false);
      handleClose();
      props.onClose();
      //reset states
      dispatch(resetUpdateApplicationPropertyStates());
    } else if (isUpdatingAppPropertyFailed) {
      setErrorMessage('Apologies, the request to change the property failed. Please try again later or contact support. Thank you');
      setSnackBarOpen(true);
    }
  }, [isUpdatingAppPropertyFailed, isUpdatingAppPropertySuccess]);

  //handlers
  const searchValueChangeHandler = (e: any) => {
    setSearchValue(e.target.value ? e.target.value.trim() : '');
  };

  const searchPropertyHandler = () => {
    dispatch({
      type: getPropertiesWithoutPagination.type,
      payload: {
        RangeSize: 1000,
        StartIndex: 0,
        SearchField: PropertySearchField.PropertyName,
        SearchValue: searchValue,
      } as PropertySearchDTO,
    });
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleNext = () => {
    const newActiveStep = activeStep + 1;
    switch (activeStep) {
      case 0:
        if (selectedProperty) {
          setActiveStep(newActiveStep);

          setSearchValue('');
        } else {
        }
        break;
      case 1:
        setActiveStep(newActiveStep);
        setSearchValue('');
        break;
      default:
        break;
    }
  };

  //reset all the states
  const handleClose = () => {
    setActiveStep(0);
    setSearchValue('');
    setIsSearchValueEmpty(false);
    setSelectedProperty(null);
    dispatch(getResetPmPropertiesState());
  };

  const handleClickSave = () => {
    if (selectedProperty) {
      dispatch({
        type: updateApplicationProperty.type,
        payload: {
          appUuid: props.appUuid,
          updateAppPropertyDto: {
            PropertyUuid: selectedProperty?.Uuid,
            PropertyName: selectedProperty?.Name,
          } as UpdateApplicationPropertyDto,
        },
      });
    }
  };

  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackBarOpen(false);
  };

  return (
    <Box>
      <Modal
        open={props.open}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        BackdropProps={{
          invisible: false,
        }}
        disableEscapeKeyDown
        onClose={props.onClose}
      >
        <Card sx={style}>
          <Grid item sx={{ display: 'flex', justifyContent: 'center' }}>
            <Typography id="subTitle" style={{ fontSize: 'medium' }}>
              Change Application Property
            </Typography>
          </Grid>
          {/* Back Icon */}
          {activeStep === 0 ? (
            <></>
          ) : (
            <Grid item sx={{ position: 'absolute', top: 18, left: 20, display: activeStep === 0 ? 'none' : '' }}>
              <img src={BackIcon} alt="close" style={{ cursor: 'pointer' }} onClick={handleBack} />
            </Grid>
          )}

          {/* Close Icon */}
          <Grid item sx={{ position: 'absolute', top: 18, right: 20 }}>
            <img
              src={CancelIcon}
              alt="close"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                handleClose();
                props.onClose();
              }}
            />
          </Grid>
          <Grid container sx={{ marginTop: '10px', marginBottom: '30px' }}>
            {/* On stepper 0, show property search input*/}
            {activeStep === 0 && (
              <Grid item mt={2} rowSpacing={2} sx={{ width: '100%', height: '100%' }}>
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', marginBottom: 2 }}>
                  <Typography style={{ fontFamily: 'Work Sans', fontWeight: 400, fontSize: '16px' }}>Search and select the new address:</Typography>
                </Grid>
                <Grid item xs={12} display="flex" justifyContent="center" width="100%" mb={1}>
                  <SearchInput
                    searchValue={searchValue}
                    searchValueChangeHandler={searchValueChangeHandler}
                    handleSearch={searchPropertyHandler}
                    isSearchValueEmpty={isSearchValueEmpty}
                    placeholder="Search by property name"
                    id="searchProperty"
                    width={matchesMobile ? '300px' : '450px'}
                    height="45px"
                  />
                </Grid>
                {isFetchingProperties ? (
                  <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }} mt={2}>
                    <CircularProgress size={40} />
                  </Grid>
                ) : (
                  <Grid
                    item
                    xs={12}
                    mx={2}
                    mb={2}
                    sx={{
                      maxHeight: '300px',
                      overflow: 'auto',
                      '&::-webkit-scrollbar': {
                        width: 7,
                      },
                      '&::-webkit-scrollbar-track': {
                        boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
                        borderRadius: '24px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: 'darkgrey',
                        borderRadius: '24px',
                      },
                    }}
                  >
                    <List>
                      {pmPropertiesWithoutPagination.map((prop) => {
                        return (
                          <Grid item xs={12} p={1} key={prop.Uuid}>
                            <Button
                              variant="contained"
                              style={{
                                width: '100%',
                                height: '46px',
                                borderRadius: '24px',
                                textTransform: 'capitalize',
                                color: 'black',
                                backgroundColor: prop.Uuid === selectedProperty?.Uuid ? '#A2DBFA' : '#F1F0F5',
                                borderColor: prop.Uuid === selectedProperty?.Uuid ? '#A2DBFA' : '#F1F0F5',
                                fontFamily: 'Work Sans',
                                fontWeight: 400,
                                fontSize: '16px',
                                lineHeight: '22px',
                                boxShadow: 'none',
                              }}
                              onClick={() => setSelectedProperty(prop)}
                              data-e2e="propertyBtn"
                            >
                              {`${prop.Name}`}
                            </Button>
                          </Grid>
                        );
                      })}
                    </List>
                  </Grid>
                )}
                <Box sx={{ position: 'absolute', bottom: matchesMobile ? 40 : 15, width: '94%' }}>
                  <Box sx={{ display: 'flex', justifyContent: matchesMobile ? 'center' : 'flex-end' }}>
                    <Button
                      disabled={!selectedProperty}
                      variant="contained"
                      style={{
                        textTransform: 'none',
                        fontFamily: "'Open Sans', sans-serif",
                        fontStyle: 'normal',
                        fontWeight: 700,
                        backgroundColor: selectedProperty ? '#39A2DB' : 'lightgrey',
                        borderRadius: '24px',
                        width: matchesMobile ? '342px' : '115px',
                      }}
                      onClick={handleNext}
                    >
                      {'Continue'}
                    </Button>
                  </Box>
                </Box>
              </Grid>
            )}
            {/* On stepper 1, show title and description inputs*/}
            {activeStep === 1 && (
              <Grid
                item
                mt={6}
                sx={{
                  maxHeight: '355px',
                  overflow: 'auto',
                  '&::-webkit-scrollbar': {
                    width: 7,
                  },
                  '&::-webkit-scrollbar-track': {
                    boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
                    borderRadius: '24px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'darkgrey',
                    borderRadius: '24px',
                  },
                }}
              >
                <Typography style={{ fontFamily: 'Work Sans', fontWeight: 400, fontSize: '16px', padding: 2 }}>
                  Updating this address will impact future notifications, screens and can also impact staff member visibility and access.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', mt: 7, ml: 10, mr: 10, gap: 4 }}>
                  <Typography style={{ fontFamily: 'Work Sans', fontWeight: 400, fontSize: '16px' }}>
                    <strong>Old:</strong> <span style={{ textDecoration: 'line-through' }}>{props.oldPropertyName}</span>
                  </Typography>
                  <Typography style={{ fontFamily: 'Work Sans', fontWeight: 400, fontSize: '16px' }}>
                    <strong>New:</strong> {`${selectedProperty?.Name || ''}`}
                  </Typography>
                </Box>
                <Typography style={{ fontFamily: 'Work Sans', fontWeight: 400, fontSize: '16px', marginTop: '50px', textAlign: 'center' }}>Are you sure you want to save changes ?</Typography>
                <Box sx={{ position: 'absolute', bottom: matchesMobile ? 40 : 15, width: '94%' }}>
                  <Box sx={{ display: 'flex', justifyContent: matchesMobile ? 'center' : 'space-between' }}>
                    <Button
                      variant="outlined"
                      color="error"
                      style={{
                        textTransform: 'none',
                        fontFamily: "'Open Sans', sans-serif",
                        fontStyle: 'normal',

                        borderRadius: '24px',
                        width: matchesMobile ? '342px' : '115px',
                      }}
                      onClick={props.onClose}
                    >
                      {'Cancel'}
                    </Button>
                    <LoadingButton
                      loading={isUpdatingAppProperty}
                      disabled={!selectedProperty}
                      variant="contained"
                      style={{
                        textTransform: 'none',
                        fontFamily: "'Open Sans', sans-serif",
                        fontStyle: 'normal',
                        backgroundColor: selectedProperty ? '#39A2DB' : 'lightgrey',
                        borderRadius: '24px',
                        width: matchesMobile ? '342px' : '115px',
                      }}
                      onClick={handleClickSave}
                    >
                      {'Save'}
                    </LoadingButton>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </Card>
      </Modal>
      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={snackBarOpen} autoHideDuration={4000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UpdateApplicationPropertyModal;
